/*
 * Copyright (c) OTB Africa. All rights reserved.
 * License terms specified in License file present in source code ./License
 */

import React from "react";
import { PaginationLimitOptions, QueryParameters } from "lib/types";
import { useDidMount } from "lib/utils";
import { useAPIRequest, UseFetchListArgs } from "lib/api/hooks";

/**
 *
 * The interface for a paginated List response
 *
 * Use Type Generics to denote that the paginated List will return results as an array of the objects with
 * the supplied Type 'T'.
 *
 */

export interface PaginatedList<T> {
  count: number;
  next: string;
  previous: string;
  results: T[];
}

/**
 *
 * @param apiServicePath The API Service to call
 * @param apiQueryParams The query parameters for the API request
 * @param dependencyList The list of items that should result in an API request when they change. Uses useEffect hooks running during a re-render.
 * @param apiPageLimit The page limit to use when calling the paginated List request. This can be stateful or not.
 * @param filterByGlobalOrganization Whether or not we should filter the list by organization selected in global scope. Note: This should always be true unless you are fetching data that does not have organization set.
 * @returns array of items for the current pagination state (i.e. limit and offset), count of total items,
 * the offset, the setAPIPageOffset update function, the nextURL, the previousURL.
 *
 */

export interface FetchPaginatedListArgs<T> extends UseFetchListArgs<T> {
  apiPageLimit?: PaginationLimitOptions;
}

export const useAPIFetchPaginatedList = <T>(args: FetchPaginatedListArgs<T>) => {
  const { dependencyList = [], apiQueryParams = {}, apiPathParams = [], apiPageLimit = 5, ...useAPIRequestArgs } = args;

  const [paginatedResponse, setPaginatedResponse] = React.useState<PaginatedList<T>>();

  // We manage offset state in this hook then the calling component can just use setAPIPageOffset to change the offset
  // If a use case comes where we need to go straight to a certain page from the initial mount, this will have to change and state lifted up.
  // Initialize to null, not 0 because an offset of 0 will trigger an api request which is not the expected behaviour from an initialized offset.
  const [apiPageOffset, setAPIPageOffset] = React.useState<number>(null);

  const { makeRequest, makeRequestCleanUp, ...othermakeRequestHookValues } = useAPIRequest<PaginatedList<T>, T, T>({
    ...useAPIRequestArgs,
  });

  const fetchPaginatedList = React.useCallback(
    (limit: number, offset: number) => {
      if (!limit) return; // Only do the fetch if limit is set. Without a limit all items will be fetched, which is not the use case for this hook and one should use the useFetchList hook.

      const paginatedAPIQueryParams = {
        limit: limit,
        offset: offset,
      };

      const resolvedQueryParams = { ...apiQueryParams, ...paginatedAPIQueryParams } as QueryParameters<T>;

      return makeRequest({
        ...useAPIRequestArgs,
        apiPathParams,
        apiQueryParams: resolvedQueryParams,
        setValidAPIResponse: setPaginatedResponse,
      });
    }, // Returning the makeRequest cleanup function so that the cleanup can be called by useEffect.
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [makeRequest, JSON.stringify(apiQueryParams), JSON.stringify(apiPathParams)]
  );

  // Running multiple useEffects here to separate concerns - https://reactjs.org/docs/hooks-effect.html#tip-use-multiple-effects-to-separate-concerns

  const didMount = useDidMount(); // Returns a ref so no need to include in dependency list.

  // Hook 1: Do fetch on initial mount

  React.useEffect(() => {
    if (useAPIRequestArgs.makeRequestOnInitialMount) setAPIPageOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hook 2: Do fetch on change query params

  // Whenever the callbacks (i.e. fetchPaginatedList (includes query parameters such as ordering and search
  // terms) & by inheritance makeRequest), limit or other passed dependencies change, set offset to zero
  // and fetch the first page so that we always receive data to display.

  React.useEffect(() => {
    if (!didMount) return;

    if (apiPageOffset !== 0) {
      // If the offset is not 0 i.e. page != 1, then we set the offset to zero first before
      // making the API request. This is necessary so that if the limit, ordering, search term etc. of the
      // api Query changes, we default back to the first page so that accurate data is displayed.
      setAPIPageOffset(0);
      return makeRequestCleanUp; // Since we are not making the actual request here (i.e. setAPIPageOffset triggers api request in useEffect for offset state), Directly Return the makeRequest cleanUp function that can be called by useEffect
    }

    return fetchPaginatedList(apiPageLimit, apiPageOffset); // Returning the makeRequest cleanup function so that the cleanup can be called by useEffect.
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPageLimit, ...dependencyList, fetchPaginatedList]);

  // Hook 3: Do fetch on change offset

  // Fetch a fresh page whenever the offset changes. TODO: see how to use one useEffect

  React.useEffect(() => {
    // Only make request if this is not the first render, because offset has an initial state of 0.
    if (!didMount) return;

    return fetchPaginatedList(apiPageLimit, apiPageOffset); // Returning the makeRequest cleanup function so that the cleanup can be called by useEffect.
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPageOffset]); // Omitting fetchPaginatedList because we do not want the dependencies to cause any unnecessary additional renders as fetchPaginatedList is already catered for in the above useEffect

  return {
    items: paginatedResponse?.results ?? [],
    apiPageOffset,
    setAPIPageOffset,
    nextURL: paginatedResponse?.next,
    previousURL: paginatedResponse?.previous,
    apiTotalItems: paginatedResponse?.count ?? 0,
    ...othermakeRequestHookValues,
  };
};
